/**
 * Chat Frontend Service
 * All HTTP calls for the chat feature via the Supabase Edge Function.
 *
 * Every function returns the `data` field from the API envelope
 * { success, data, error } so callers receive the payload directly.
 */

import { getSupabaseSession } from '../../../services/supabaseClient';
import { SUPABASE_FUNCTIONS_URL } from '../../../utils/apiEndpoints';

const CHAT_FUNCTION_URL = `${SUPABASE_FUNCTIONS_URL}/chat`;

async function getAuthToken() {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('Je moet ingelogd zijn om berichten te bekijken.');
  }
  return session.access_token;
}

async function request(path, { method = 'GET', body } = {}) {
  const token = await getAuthToken();
  const response = await fetch(`${CHAT_FUNCTION_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const result = await response.json().catch(() => null);
  if (!response.ok || result?.success === false) {
    throw new Error(result?.error?.message || 'Er ging iets mis bij het laden van de chat.');
  }

  return result.data;
}

/**
 * Get all conversations for the authenticated user.
 * @returns {Promise<ConversationSummary[]>}
 */
const getConversations = () =>
  request('/conversations').then(data => data.conversations);

/**
 * Get a single conversation by ID.
 * @param {string} conversationId
 * @returns {Promise<ConversationDetail>}
 */
const getConversation = (conversationId) =>
  request(`/conversations/${conversationId}`).then(data => data.conversation);

/**
 * Get messages for a conversation with cursor-based pagination.
 * cursor: sentAt of the oldest currently-loaded message (page backward).
 * limit: max messages to return (default 30, max 50).
 *
 * @param {string} conversationId
 * @param {{ cursor?: string|null, limit?: number }} options
 * @returns {Promise<{ messages: Message[], hasMore: boolean, nextCursor: string|null }>}
 */
const getMessages = (conversationId, { cursor, limit } = {}) => {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  return request(`/conversations/${conversationId}/messages${qs}`);
};

/**
 * Send a text message to a conversation.
 * @param {string} conversationId
 * @param {{ content: string, replyToId?: string|null }} payload
 * @returns {Promise<Message>}
 */
const sendMessage = (conversationId, { content, replyToId }) =>
  request(`/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: {
      content,
      replyToId: replyToId || null,
    },
  }).then(data => data.message);

/**
 * Mark all unread messages in a conversation as read.
 * @param {string} conversationId
 * @returns {Promise<{ updated: boolean }>}
 */
const markAsRead = (conversationId) =>
  request(`/conversations/${conversationId}/mark-read`, { method: 'PUT', body: {} });

/**
 * Soft-delete a message (sender only).
 * @param {string} messageId
 * @returns {Promise<{ deleted: boolean }>}
 */
const deleteMessage = (messageId) =>
  request(`/messages/${messageId}`, { method: 'DELETE' });

/**
 * Get total unread count + per-conversation breakdown.
 * @returns {Promise<{ total: number, byConversation: Object<string, number> }>}
 */
const getUnreadCount = () =>
  request('/unread-count');

export const chatService = {
  getConversations,
  getConversation,
  getMessages,
  sendMessage,
  markAsRead,
  deleteMessage,
  getUnreadCount,
};
